import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000BasicDialog from "../../../u2000Core/components/dialog/U2000BasicDialog";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import { NS } from "../../U3101";
import {
  U3101_HOUR_BANK_TYPES,
  type U3101HourBankBulkCloneMode,
  type U3101HourBankBulkCloneResult,
  type U3101HourBankType,
} from "../../U3101Service";
import { useU3101HourBankHistoryHandlers } from "../../U3101UseHourBankHistoryHandlers";
import { u3101BuildHourBankBulkClonePayload } from "../../tools/U3101HourBankBulkClonePayload";

export interface U3101HourBankBulkCloneDlgProps {
  isOpen: boolean;
  onClose: () => void;
  onDone?: (result: U3101HourBankBulkCloneResult) => void;
}

type BankDraft = {
  initialHours: string;
  overrideEmployee: boolean;
};

function emptyBankDrafts(): Record<U3101HourBankType, BankDraft> {
  return {
    vacation: { initialHours: "0", overrideEmployee: false },
    holidayLeave: { initialHours: "0", overrideEmployee: false },
    sickLeave: { initialHours: "0", overrideEmployee: false },
    special: { initialHours: "0", overrideEmployee: false },
  };
}

function bankTypeLabelKey(bankType: U3101HourBankType): string {
  switch (bankType) {
    case "vacation":
      return "lblHourBankTypeVacation";
    case "holidayLeave":
      return "lblHourBankTypeHolidayLeave";
    case "sickLeave":
      return "lblHourBankTypeSickLeave";
    case "special":
      return "lblHourBankTypeSpecial";
  }
}

const U3101HourBankBulkCloneDlg: React.FC<U3101HourBankBulkCloneDlgProps> = ({
  isOpen,
  onClose,
  onDone,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { cloneNextBulk } = useU3101HourBankHistoryHandlers();
  const [mode, setMode] = useState<U3101HourBankBulkCloneMode>("fromPrevious");
  const [drafts, setDrafts] = useState(emptyBankDrafts);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMode("fromPrevious");
      setDrafts(emptyBankDrafts());
      setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    const built = u3101BuildHourBankBulkClonePayload(mode, drafts);
    if (!built.ok) {
      toast.error(t("errHourBankInitialInvalid", { ns: NS }));
      return;
    }
    const payload = built.payload;
    setSubmitting(true);
    const result = await cloneNextBulk(payload);
    setSubmitting(false);
    if (!result) return;

    toast.success(
      t("msgHourBankBulkCloneSuccess", {
        ns: NS,
        created: result.created,
        skipped: result.skippedAlreadyExists,
        processed: result.processed,
        sourceYear: result.sourceYear,
        targetYear: result.targetYear,
      }),
    );
    if (result.errors.length > 0) {
      toast.error(
        t("msgHourBankBulkClonePartialErrors", {
          ns: NS,
          count: result.errors.length,
        }),
      );
    }
    onDone?.(result);
    onClose();
  };

  return (
    <U2000BasicDialog
      isOpen={isOpen}
      onClose={onClose}
      title={t("dlgHourBankBulkCloneTitle", { ns: NS })}
      width="medium"
    >
      <div className="space-y-4 px-6 py-4">
        <p className="text-sm text-gray-600">{t("dlgHourBankBulkCloneIntro", { ns: NS })}</p>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-gray-800">
            {t("lblHourBankBulkCloneMode", { ns: NS })}
          </legend>
          <label className="flex cursor-pointer items-start gap-2 text-sm text-gray-700">
            <input
              type="radio"
              name="hourBankBulkMode"
              className="mt-1"
              checked={mode === "fromPrevious"}
              disabled={submitting}
              onChange={() => setMode("fromPrevious")}
            />
            <span>
              <span className="font-medium">{t("valHourBankBulkModeFromPrevious", { ns: NS })}</span>
              <span className="mt-0.5 block text-xs text-gray-500">
                {t("hintHourBankBulkModeFromPrevious", { ns: NS })}
              </span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 text-sm text-gray-700">
            <input
              type="radio"
              name="hourBankBulkMode"
              className="mt-1"
              checked={mode === "custom"}
              disabled={submitting}
              onChange={() => setMode("custom")}
            />
            <span>
              <span className="font-medium">{t("valHourBankBulkModeCustom", { ns: NS })}</span>
              <span className="mt-0.5 block text-xs text-gray-500">
                {t("hintHourBankBulkModeCustom", { ns: NS })}
              </span>
            </span>
          </label>
        </fieldset>

        {mode === "custom" ? (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2 font-medium">{t("colHourBankType", { ns: NS })}</th>
                  <th className="px-3 py-2 font-medium">{t("colHourBankInitial", { ns: NS })}</th>
                  <th className="px-3 py-2 font-medium">{t("lblHourBankBulkOverride", { ns: NS })}</th>
                </tr>
              </thead>
              <tbody>
                {U3101_HOUR_BANK_TYPES.map((bankType) => (
                  <tr key={bankType} className="border-t border-gray-100">
                    <td className="px-3 py-2 text-gray-700">
                      {t(bankTypeLabelKey(bankType), { ns: NS })}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        inputMode="decimal"
                        disabled={submitting}
                        className="w-28 rounded-sm border border-gray-300 px-2 py-1 text-right text-sm tabular-nums focus:border-blue-500 focus:outline-hidden focus:ring-1 focus:ring-blue-500/30"
                        value={drafts[bankType].initialHours}
                        onChange={(e) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [bankType]: { ...prev[bankType], initialHours: e.target.value },
                          }))
                        }
                      />
                    </td>
                    <td className="px-3 py-2">
                      <label className="inline-flex items-center gap-2 text-xs text-gray-600">
                        <input
                          type="checkbox"
                          disabled={submitting}
                          checked={drafts[bankType].overrideEmployee}
                          onChange={(e) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [bankType]: {
                                ...prev[bankType],
                                overrideEmployee: e.target.checked,
                              },
                            }))
                          }
                        />
                        {t("lblHourBankBulkOverrideShort", { ns: NS })}
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <U2000ActionBtn
            label={t("btnCancel", { ns: NS })}
            handleClick={onClose}
            variant="secondary"
            disabled={submitting}
          />
          <U2000ActionBtn
            label={t("btnConfirmHourBankBulkClone", { ns: NS })}
            handleClick={() => void handleSubmit()}
            disabled={submitting}
          />
        </div>
      </div>
    </U2000BasicDialog>
  );
};

export default U3101HourBankBulkCloneDlg;
